import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../../auth/useAuth";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { ActionType } from "../../../../models/ActionType";
import { AppSettingsTabProps, TDocumentTemplate } from "../../models";
import { useAppTemplates } from "../../hooks/documentTemplates/useAppTemplates";
import { useGetTemplateFile } from "../../hooks/documentTemplates/useGetTemplateFile";
import { DataGrid } from "../../../ui/components/DataGrid";
import { FunctionViewField } from "../../../ui/components/viewField/FunctionViewField";
import { ListContainer } from "../../../ui/containers/ListContainer";
import {
  companyStatusesModel,
  getCompanyStatusIcon,
} from "../../../company/utils";
import { TemplateDialog } from "../TemplateModal";

export const TemplatesTab = ({
  appId,
  config,
  onConfigChange,
}: AppSettingsTabProps) => {
  const { companyRole, isSuperAdmin } = useAuth();
  const [
    templatesList,
    templatesListLoading,
    templatesListError,
    templatesListReload,
  ] = useAppTemplates(appId);
  const [downloadLoading, downloadError, downloadTemplate] = useGetTemplateFile();
  const [isShowTemplateDialog, setIsShowTemplateDialog] = useState(false);
  const [templateDialogMode, setTemplateDialogMode] = useState<ActionType>(
    ActionType.CREATE,
  );
  const [selectedTemplate, setIsSelectedTemplate] =
    useState<TDocumentTemplate | null>(null);

  const handleOpenAction = useCallback((mode: ActionType, template?: TDocumentTemplate) => {
    setTemplateDialogMode(mode);
    if (template) {
      setIsSelectedTemplate(template);
    }
    setIsShowTemplateDialog(true);
  }, []);

  const handleClose = useCallback(
    (success: boolean) => {
      if (success) {
        templatesListReload();
      }
      setIsShowTemplateDialog(false);
      setIsSelectedTemplate(null);
    },
    [templatesListReload],
  );

  const handleDownload = useCallback(
    async (templateId: string) => {
      try {
        await downloadTemplate(templateId);
      } catch (e) {
        console.error("Failed to download template:", e);
      }
    },
    [downloadTemplate],
  );

  const changeIsSigningActive = useCallback(() => {
    onConfigChange((prev) => ({
      ...prev,
      document_signing: !(prev.document_signing || false),
    }));
  }, [onConfigChange]);

  return (
    <>
      <Paper
        sx={{
          margin: "12px 0",
          padding: "20px 32px",
          background: "#ffffff",
          borderRadius: "14px",
          border: "none",
        }}
      >
        <Stack direction='row' spacing={2}>
          <FormControlLabel
            control={<Checkbox />}
            label='Add the document signing step in the onboarding process'
            name='doc'
            checked={config.document_signing || false}
            onChange={changeIsSigningActive}
          />
        </Stack>
        <Stack direction='row' spacing={2} justifyContent='end'>
          <Button
            variant='contained'
            sx={{ mt: 2, mb: 2 }}
            onClick={() => handleOpenAction(ActionType.CREATE)}
            disabled={
              templatesListLoading ||
              !(companyRole === CompanyRole.ADMIN || isSuperAdmin)
            }
          >
            Add template
          </Button>
        </Stack>
        {(templatesListError || downloadError) && (
          <Typography color='error' sx={{ mb: 2 }}>
            {(templatesListError || downloadError)?.toString()}
          </Typography>
        )}
        <ListContainer data={templatesList.items} isLoading={templatesListLoading}>
          <DataGrid>
            <FunctionViewField
              label='Name'
              source='name'
              render={(record: TDocumentTemplate) => (
                <Box>
                  <Typography variant='h6'>{record?.name}</Typography>
                  <Typography variant='body2'>ID: {record?.id}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='Description'
              source='description'
              render={(record: TDocumentTemplate) => (
                <Box>
                  <Typography variant='body2'>{record?.description}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='Sort order'
              source='sortOrder'
              render={(record: TDocumentTemplate) => (
                <Box>
                  <Typography variant='body2'>{record?.sortOrder}</Typography>
                </Box>
              )}
            />
            <FunctionViewField
              label='Status'
              source='status'
              render={(record: TDocumentTemplate) => (
                <Stack
                  direction='row'
                  justifyContent='flex-start'
                  alignItems='center'
                  spacing={1}
                  minWidth='200px'
                >
                  <Box
                    sx={{
                      padding: "5px 10px",
                      background: companyStatusesModel[String(record.isActive)]?.bgColor,
                      borderRadius: "6px",
                    }}
                  >
                    <Stack
                      direction='row'
                      justifyContent='center'
                      alignItems='center'
                      position='relative'
                    >
                      <Box position='absolute' top='0' left='0'>
                        {getCompanyStatusIcon(record.isActive)}
                      </Box>
                      <Typography
                        variant='body2'
                        sx={{
                          paddingLeft: "25px",
                          fontWeight: "700",
                          color: companyStatusesModel[String(record.isActive)]
                            ?.textColor,
                        }}
                      >
                        {companyStatusesModel[String(record.isActive)]?.name}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            />
            <FunctionViewField
              label='Actions'
              source='actions'
              render={(record: TDocumentTemplate) => (
                <Box>
                  <Button
                    onClick={() => handleDownload(record.id)}
                    disabled={downloadLoading}
                  >
                    Download
                  </Button>
                  <Button
                    disabled={!(companyRole === CompanyRole.ADMIN || isSuperAdmin)}
                    onClick={() => handleOpenAction(ActionType.EDIT, record)}
                  >
                    Edit
                  </Button>
                  <Button
                    disabled={!(companyRole === CompanyRole.ADMIN || isSuperAdmin)}
                    onClick={() => handleOpenAction(ActionType.DELETE, record)}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            />
          </DataGrid>
        </ListContainer>
      </Paper>
      <TemplateDialog
        open={isShowTemplateDialog}
        actionType={templateDialogMode}
        onClose={handleClose}
        selectedTemplate={selectedTemplate}
      />
    </>
  );
};
